import React from 'react';
import {
  HStack,
  Text,
  Badge,
  BadgeText,
  Button,
  ButtonText,
  ButtonSpinner,
  Icon,
  Menu,
  MenuItem,
  MenuItemLabel,
  Pressable,
} from '@gluestack-ui/themed';
import {
  BookOpen, MoreHorizontal, Eye, Plus, Trash2, Send, ArrowLeft, WandSparkles,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import type { LecturerModule } from '@/common/navigation/RoleNavigations/Lecturer/types';
import type { ModuleLecturerResponseVersionHistory } from '@/common/api/models/moduleLecturerResponseVersionHistory';
import VersionSelector from './VersionSelector';

interface ModuleCommandStripProps {
  module: LecturerModule | null;
  versionHistory: ModuleLecturerResponseVersionHistory | null;
  selectedVersion: string | null;
  onVersionChange: (version: string) => void;
  isLoading: boolean;
  onPreview: () => void;
  onCreateVersion: () => void;
  onDeleteVersion: () => void;
  onDeleteModule: () => void;
  onRequestApproval: () => void;
  onSave: () => void;
  onBack?: () => void;
  formIsDirty: boolean;
  isReadOnly: boolean;
  isAnyMutationPending: boolean;
  /** Whether the current user may create a new version (author + settled draft/published). */
  canCreateVersion: boolean;
  /** Whether the current user may delete the selected draft version. */
  canDeleteVersion: boolean;
  /** Whether the current user may delete the whole module. */
  canDeleteModule: boolean;
  /** Whether the current user may request approval for the draft. */
  canRequestApproval: boolean;
  /** Whether the current user may launch AI rubric generation. */
  canGenerateRubrics: boolean;
  /** Combined pending state for module and task save mutations */
  saveMutationPending: boolean;
  createVersionMutationPending: boolean;
  deleteVersionMutationPending: boolean;
  deleteModuleMutationPending: boolean;
  requestApprovalMutationPending: boolean;
  onGenerateAllRubrics?: () => void;
  isGeneratingRubrics?: boolean;
}

/**
 * Command strip for module editor - displays module info, version, and actions.
 */
const ModuleCommandStrip: React.FC<ModuleCommandStripProps> = ({
  module,
  versionHistory,
  selectedVersion,
  onVersionChange,
  isLoading,
  onPreview,
  onCreateVersion,
  onDeleteVersion,
  onDeleteModule,
  onRequestApproval,
  onSave,
  onBack,
  formIsDirty,
  isReadOnly,
  isAnyMutationPending,
  canCreateVersion,
  canDeleteVersion,
  canDeleteModule,
  canRequestApproval,
  canGenerateRubrics,
  saveMutationPending,
  createVersionMutationPending,
  deleteVersionMutationPending,
  deleteModuleMutationPending,
  requestApprovalMutationPending,
  onGenerateAllRubrics,
  isGeneratingRubrics = false,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigation();

  if (!module) return null;

  const workflowState = module.state?.workflowState || 'draft';

  const getBadgeAction = (): 'success' | 'warning' | 'info' | 'muted' | 'error' => {
    switch (workflowState) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'pending_approval':
        return 'info';
      case 'retired':
        return 'muted';
      default:
        return 'info';
    }
  };

  const isGenerateRubricsDisabled = isAnyMutationPending || isGeneratingRubrics || isReadOnly;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Use explicit navigation to the home base if goBack is ambiguous
      navigation.navigate({ name: 'EvaluationDesk', params: {}, merge: false } as never);
    }
  };

  return (
    <HStack
      h={56}
      bg="$bgApp"
      borderBottomWidth={1}
      borderBottomColor="$borderLight"
      alignItems="center"
      justifyContent="space-between"
      px="$4"
    >
      {/* Left side: Back button + Module info */}
      <HStack alignItems="center" space="lg" flex={1}>
        <Pressable
          onPress={handleBack}
          p="$2"
          borderRadius="$full"
          $hover={{ bg: '$bgHover' }}
          aria-label={t('common.buttons.back')}
        >
          <Icon as={ArrowLeft} size="md" color="$textSubtle" />
        </Pressable>

        <HStack space="sm" alignItems="center">
          <Icon as={BookOpen} size="sm" color="$brandBase" />
          <Text size="sm" fontWeight="bold" color="$textHeading" numberOfLines={1}>
            {module.title || 'Untitled Module'}
          </Text>
        </HStack>
        <VersionSelector
          versionHistory={versionHistory}
          selectedVersion={selectedVersion}
          onVersionChange={onVersionChange}
          isLoading={isLoading}
        />

        <Badge size="sm" variant="solid" action={getBadgeAction()} borderRadius="$sm">
          <BadgeText>{t(`lecturer.moduleEditor.moduleEditor.versionSelector.states.${workflowState}`)}</BadgeText>
        </Badge>
      </HStack>

      {/* Right side: Actions */}
      <HStack alignItems="center" space="sm">
        {(formIsDirty || saveMutationPending) && (
          <Button size="sm" onPress={onSave} isDisabled={saveMutationPending}>
            {saveMutationPending && <ButtonSpinner mr="$2" />}
            <ButtonText>{t('common.buttons.save')}</ButtonText>
          </Button>
        )}

        {!formIsDirty && !saveMutationPending && (
          <Button size="sm" onPress={onPreview} variant="outline" action="secondary" isDisabled={isAnyMutationPending}>
            <Icon as={Eye} size="xs" mr="$1" />
            <ButtonText>{t('lecturer.moduleEditor.moduleEditor.buttons.preview')}</ButtonText>
          </Button>
        )}

        {canRequestApproval && (
          <Button
            size="sm"
            onPress={onRequestApproval}
            action="primary"
            isDisabled={isReadOnly || requestApprovalMutationPending}
          >
            {requestApprovalMutationPending && <ButtonSpinner mr="$2" />}
            <Icon as={Send} size="xs" mr="$1" color="$textInverted" />
            <ButtonText>{t('lecturer.moduleEditor.moduleEditor.buttons.requestApproval')}</ButtonText>
          </Button>
        )}

        <Menu
          placement="bottom right"
          trigger={({ ...triggerProps }) => (
            <Pressable
              {...triggerProps}
              p="$2"
              borderRadius="$full"
              $hover={{ bg: '$bgHover' }}
              testID="module-editor-overflow-menu"
            >
              <Icon as={MoreHorizontal} size="sm" color="$textSubtle" />
            </Pressable>
          )}
        >
          {canCreateVersion && (
            <MenuItem
              key="create-version"
              onPress={onCreateVersion}
              disabled={isAnyMutationPending || createVersionMutationPending}
            >
              {createVersionMutationPending ? (
                <ButtonSpinner mr="$2" />
              ) : (
                <Icon as={Plus} size="sm" color="$textSubtle" mr="$2" />
              )}
              <MenuItemLabel>
                {t('lecturer.moduleEditor.moduleEditor.buttons.createModuleVersion')}
              </MenuItemLabel>
            </MenuItem>
          )}

          {canDeleteVersion && (
            <MenuItem
              key="delete"
              onPress={onDeleteVersion}
              disabled={isAnyMutationPending || deleteVersionMutationPending}
            >
              <Icon as={Trash2} size="sm" color="$statusError" mr="$2" />
              <MenuItemLabel color="$statusError">
                {t('lecturer.moduleEditor.moduleEditor.buttons.deleteVersion')}
              </MenuItemLabel>
            </MenuItem>
          )}

          <MenuItem
            key="delete-module"
            onPress={onDeleteModule}
            disabled={!canDeleteModule || isAnyMutationPending || deleteModuleMutationPending}
          >
            <Icon as={Trash2} size="sm" color={canDeleteModule ? '$statusError' : '$textDisabled'} mr="$2" />
            <MenuItemLabel color={canDeleteModule ? '$statusError' : '$textDisabled'}>
              {t('lecturer.moduleEditor.moduleEditor.buttons.deleteModule', {
                defaultValue: 'Delete Entire Module',
              })}
            </MenuItemLabel>
          </MenuItem>

          {canGenerateRubrics && onGenerateAllRubrics && (
            <MenuItem
              key="generate-all-rubrics"
              onPress={onGenerateAllRubrics}
              disabled={isGenerateRubricsDisabled}
            >
              <Icon as={WandSparkles} size="sm" color={isGenerateRubricsDisabled ? '$textDisabled' : '$textSubtle'} mr="$2" />
              <MenuItemLabel color={isGenerateRubricsDisabled ? '$textDisabled' : undefined}>
                {t('lecturer.moduleEditor.moduleEditor.buttons.generateAllRubrics')}
              </MenuItemLabel>
            </MenuItem>
          )}
        </Menu>
      </HStack>
    </HStack>
  );
};

export default ModuleCommandStrip;
